import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { selectCurrentUser } from '../selectors';
import { updateProfile } from '../userSlice';

export default function ProfilePage() {
  const { t } = useTranslation();
  const user = useAppSelector(selectCurrentUser);
  const dispatch = useAppDispatch();
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [saved, setSaved] = useState(false);

  if (!user) return null;
  const dirty = name !== user.name || email !== user.email || phone !== user.phone;
  // SC-421: a name must contain at least one letter — reject values made only of
  // digits/spaces/punctuation (e.g. "6666 6666"). \p{L} matches any Unicode letter
  // so accented, CJK and hyphenated names stay valid.
  const nameInvalid = name.trim() !== '' && !/\p{L}/u.test(name);

  const onSave = () => {
    if (!dirty || nameInvalid) return;
    dispatch(updateProfile({ name, email, phone }));
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  return (
    <div className="space-y-6">
      <div className="bg-paper border-2 border-ink shadow-stamp p-5 rounded-btn space-y-3">
        <h2 className="font-display text-xl">{t('profileForm.informations')}</h2>
        <Field label={t('reservation.namePlaceholder')}><input value={name} onChange={e => setName(e.target.value)} aria-invalid={nameInvalid} className="w-full px-3 py-2 bg-paper border-2 border-ink rounded-btn-sm" /></Field>
        {nameInvalid && (
          <p className="text-sm text-hot-pink" role="alert">{t('profileForm.nameInvalid')}</p>
        )}
        <Field label={t('reservation.emailPlaceholder')}><input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-3 py-2 bg-paper border-2 border-ink rounded-btn-sm" /></Field>
        <Field label={t('reservation.phonePlaceholder')}><input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="w-full px-3 py-2 bg-paper border-2 border-ink rounded-btn-sm" /></Field>
        <button type="button" onClick={onSave} disabled={!dirty || nameInvalid}
          className="w-full py-3 bg-ink text-paper font-display rounded-btn shadow-stamp-sm disabled:opacity-40">
          {saved ? t('profileForm.saved') : t('profileForm.save')}
        </button>
      </div>

      <div className="bg-paper border-2 border-ink shadow-stamp p-5 rounded-btn">
        <h2 className="font-display text-xl mb-3">{t('profileForm.googleAccount')}</h2>
        {user.google.connected ? (
          <p className="text-ink">{t('profileForm.connectedVia')} <strong>{user.google.email}</strong></p>
        ) : (
          <p className="text-ink/70">{t('profileForm.noGoogle')}</p>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-display text-ink mb-1">{label}</span>
      {children}
    </label>
  );
}
